import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/DevelopmentForm.css";
import { developmentWorksAPI, fileAPI, galleryAPI } from "../../api/index";

const MANDALS = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];
const CATEGORIES = ["Road Works", "Water Supply", "Drainage"];
const STATUSES = ["pending", "in_progress", "completed"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES = 10;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Fields that show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["village_name", "estimated_cost"];

const INITIAL_FORM = {
  mandal_name: "",
  village_name: "",
  category: "",
  title: "",
  estimated_cost: "",
  description: "",
  status: "pending",
  work_date: "",
};

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "mandal_name":
      if (!v) return "Please select a Mandal";
      if (!MANDALS.includes(v)) return "Please select a valid Mandal";
      return "";

    case "category":
      if (!v) return "Please select a Category";
      if (!CATEGORIES.includes(v)) return "Please select a valid Category";
      return "";

    case "village_name":
      if (!v) return "Village name is required";
      if (!/^[A-Za-z\s]+$/.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "title":
      if (!v) return "Development title is required";
      if (v.length < 3) return "Title must be at least 3 characters";
      if (v.length > 150) return "Title must not exceed 150 characters";
      return "";

    case "estimated_cost":
      if (!v) return "Estimated cost is required";
      if (!/^\d+(\.\d{1,2})?$/.test(v))
        return "Enter numbers only (e.g. 250000 or 250000.50)";
      if (Number(v) <= 0) return "Estimated cost must be greater than 0";
      if (Number(v) > 1000000000000) return "Estimated cost is too large";
      return "";

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUSES.includes(v)) return "Please select a valid Status";
      return "";

    case "work_date": {
      if (!v) return "Work date is required";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "description":
      if (!v) return ""; // optional
      if (v.length < 10) return "Description must be at least 10 characters";
      if (v.length > 1000) return "Description must not exceed 1000 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (form) => {
  const errors = {};
  Object.keys(INITIAL_FORM).forEach((name) => {
    const msg = validateField(name, form[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

function DevelopmentForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [images, setImages] = useState([]); // { file, url, name, status: 'pending'|'uploading'|'done'|'error' }
  const [imageError, setImageError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = [];
    const rejected = [];

    for (const file of files) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        rejected.push(`${file.name}: only JPG, PNG, WEBP allowed`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name}: exceeds 5MB`);
        continue;
      }
      valid.push({
        file,
        url: URL.createObjectURL(file),
        name: file.name,
        status: "pending",
      });
    }

    const total = images.length + valid.length;
    if (total > MAX_IMAGES) {
      const allowed = Math.max(0, MAX_IMAGES - images.length);
      rejected.push(`Maximum ${MAX_IMAGES} images allowed`);
      valid.splice(allowed);
    }

    if (valid.length > 0) {
      setImages((prev) => [...prev, ...valid]);
    }

    // Inline error: show rejected files, otherwise clear the "required" error
    if (rejected.length > 0) {
      setImageError(rejected.join(" | "));
    } else {
      setImageError("");
    }

    e.target.value = ""; // allows selecting the same file again
  };

  const removeImage = (index) => {
    const remaining = images.filter((_, i) => i !== index);
    setImages(remaining);
    if (remaining.length === 0 && Object.keys(touched).length > 0) {
      setImageError("Please upload at least one image");
    } else {
      setImageError("");
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setTouched({});
    setImages([]);
    setImageError("");
  };

  // Uploads every selected image via the real POST /files/upload endpoint,
  // then registers each as a Gallery entry (POST /gallery) tagged with the
  // development work's title. This is done because DevelopmentWorkCreate/Out
  // has no image field of its own to attach a URL to — Gallery is a real,
  // separate multimedia table that does store images.
  const uploadImages = async (workTitle) => {
    const uploaded = [];
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, status: "uploading" } : im)));
      try {
        const uploadRes = await fileAPI.upload(img.file, "development-works");
        // Field name guessed — CONFIRM the real /files/upload response shape
        // with backend. Common shapes: { url }, { file_url }, { path }.
        const fileUrl =
          uploadRes.data?.url || uploadRes.data?.file_url || uploadRes.data?.path || uploadRes.data;

        // CONFIRM galleryAPI.create's real expected field names
        // (GalleryPhotoCreate schema) — guessed below.
        await galleryAPI.create({
          title: `${workTitle} — photo ${i + 1}`,
          image_url: fileUrl,
          description: `Development work photo: ${workTitle}`,
        });

        uploaded.push(fileUrl);
        setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, status: "done" } : im)));
      } catch (err) {
        console.error(`Image upload failed for ${img.name}:`, err.response?.data || err.message);
        setImages((prev) => prev.map((im, idx) => (idx === i ? { ...im, status: "error" } : im)));
        toast.error(`Could not upload ${img.name}`);
      }
    }
    return uploaded;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(form);
    setErrors(newErrors);
    setTouched(Object.keys(INITIAL_FORM).reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    // Validate images (at least one required)
    const imgErr = images.length === 0 ? "Please upload at least one image" : "";
    setImageError(imgErr);

    const errorFields = Object.keys(newErrors);
    if (errorFields.length > 0 || imgErr) {
      toast.error("Please fix the highlighted fields");
      if (errorFields.length > 0) {
        const firstField = document.querySelector(`[name="${errorFields[0]}"]`);
        if (firstField) firstField.focus();
      } else {
        const section = document.getElementById("upload-section");
        if (section) section.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        mandal_name: form.mandal_name,
        status: form.status,
        category: form.category,
        village_name: form.village_name.trim(),
        estimated_cost: Number(form.estimated_cost),
        work_date: form.work_date,
      };
      if (form.description.trim()) payload.description = form.description.trim();

      await developmentWorksAPI.create(payload);

      if (images.length > 0) {
        toast.info(`Uploading ${images.length} image(s)...`);
        await uploadImages(form.title.trim());
      }

      toast.success("Development work added successfully");
      handleReset();
      navigate("/dashboard/development");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((d) => d.msg || d.reason).join("; ") :
        "Could not save development work"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="development-form">
      <div className="form-header">
        <div>
          <h2>Add Development Work</h2>
          <p>Enter development details and upload images to display on website</p>
        </div>
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {/* noValidate -> disables browser default popups so our custom messages show */}
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-grid">
          <div className="form-group">
            <label>Mandal / Area *</label>
            <select
              name="mandal_name"
              value={form.mandal_name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("mandal_name")}
            >
              <option value="">Select Mandal</option>
              {MANDALS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {showError("mandal_name") && <span className="error-text">{errors.mandal_name}</span>}
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("category")}
            >
              <option value="">Select Category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {showError("category") && <span className="error-text">{errors.category}</span>}
          </div>

          <div className="form-group">
            <label>Village *</label>
            <input
              type="text"
              name="village_name"
              placeholder="Enter village name (alphabets only)"
              value={form.village_name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("village_name")}
              maxLength={100}
            />
            {showError("village_name") && <span className="error-text">{errors.village_name}</span>}
          </div>

          <div className="form-group">
            <label>Development Title *</label>
            <input
              type="text"
              name="title"
              placeholder="Enter title of the development work"
              value={form.title}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("title")}
              maxLength={150}
            />
            {showError("title") && <span className="error-text">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label>Estimated Cost (₹) *</label>
            {/* type="text" + inputMode="decimal" so we can show our own error for non-numeric input */}
            <input
              type="text"
              inputMode="decimal"
              name="estimated_cost"
              placeholder="Enter estimated cost (numbers only)"
              value={form.estimated_cost}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("estimated_cost")}
              maxLength={16}
            />
            {showError("estimated_cost") && <span className="error-text">{errors.estimated_cost}</span>}
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("status")}
            >
              <option value="">Select Status</option>
              <option value="pending">Proposed / Pending</option>
              <option value="in_progress">Ongoing</option>
              <option value="completed">Completed</option>
            </select>
            {showError("status") && <span className="error-text">{errors.status}</span>}
          </div>

          <div className="form-group">
            <label>Work Date *</label>
            <input
              type="date"
              name="work_date"
              value={form.work_date}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("work_date")}
              min="1990-01-01"
              max="2100-12-31"
            />
            {showError("work_date") && <span className="error-text">{errors.work_date}</span>}
          </div>

          <div className="form-group full-width">
            <label>Description (Optional)</label>
            <textarea
              rows="5"
              name="description"
              placeholder="Enter description of the development work"
              value={form.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("description")}
              maxLength={1000}
            />
            {showError("description") && <span className="error-text">{errors.description}</span>}
            <small className="char-count">{form.description.length}/1000</small>
          </div>
        </div>

        {/* Upload Section — real upload via POST /files/upload + POST /gallery */}
        <div className="upload-section" id="upload-section">
          <div className="upload-box">
            <h4>Upload Images *</h4>
            <div className={`drop-zone ${imageError ? "drop-zone-error" : ""}`}>
              <p>Drag & Drop Images Here</p>
              <input
                type="file"
                id="images"
                multiple
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={handleFilesSelected}
              />
              <label htmlFor="images" className="choose-btn">Choose Files</label>
              <small>JPG, PNG, WEBP (Max 5MB each, up to {MAX_IMAGES} images) — uploaded to Gallery on submit</small>
            </div>
            {imageError && <span className="error-text">{imageError}</span>}
          </div>

          <div className="preview-box">
            <h4>Uploaded Images ({images.length})</h4>
            {images.length === 0 ? (
              <div className="preview-area">No Images Uploaded</div>
            ) : (
              <div className="preview-area" style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {images.map((img, i) => (
                  <div key={i} style={{ position: "relative", width: 80, height: 80 }}>
                    <img
                      src={img.url}
                      alt={img.name}
                      style={{
                        width: "100%", height: "100%", objectFit: "cover", borderRadius: 8,
                        opacity: img.status === "error" ? 0.4 : 1,
                        border:
                          img.status === "done" ? "2px solid #16a34a" :
                          img.status === "error" ? "2px solid #dc2626" :
                          img.status === "uploading" ? "2px solid #eab308" : "2px solid transparent",
                      }}
                    />
                    {img.status === "uploading" && (
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)", borderRadius: 8, color: "#fff", fontSize: 10 }}>
                        ⏳
                      </div>
                    )}
                    {img.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        style={{
                          position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%",
                          background: "#dc2626", color: "#fff", border: "none", fontSize: 12, cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="button-group">
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
          <button type="button" className="reset-btn" onClick={handleReset}>
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}

export default DevelopmentForm;